/**
 * NebulaMaterial — a raymarched emissive gas cloud for the space sky (shader
 * wave stage 4, 2026-09-09).
 *
 * The in-scene-volume method (Maxime Heckel's "Real-time dreamy cloudscapes",
 * the Godot "Volumetric nebulae/clouds" shader, SpaceEngine's raymarched
 * nebulae): the mesh is a sphere; the fragment marches the view ray through
 * the sphere in LOCAL space (so the nebula rides its object), samples fBm
 * density, and integrates emission with Beer–Lambert absorption — dense
 * cores glow the warm colour, the thin envelope the cool one, transmittance
 * falls with density so the far side never over-brightens. Front faces are
 * drawn (the camera is outside), additive, no depth write.
 *
 * `nebulaDensityCpu` and `beerLambertCpu` are the oracles the tests pin.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, vec4, positionLocal, cameraPosition, modelWorldMatrixInverse,
  mx_fractal_noise_float, saturate, mix, Loop, exp, dot, sqrt, max, normalize, pow, abs, length,
} from 'three/tsl';
import { markMrtOverlay } from '../../post/mrtOverlay.js';

export interface NebulaConfig {
  /** March steps through the sphere (default 20). */
  steps?: number;
  /** Core (dense) colour. */
  coreColor?: [number, number, number];
  /** Envelope (thin) colour. */
  envelopeColor?: [number, number, number];
  /** Density multiplier (default 1). */
  density?: number;
  /** Absorption per unit density (default 1.6). */
  absorption?: number;
  /** Noise frequency in sphere radii (default 2.2). */
  scale?: number;
  /** Emission multiplier (default 1). */
  intensity?: number;
  /**
   * v2 (2026-09-25, the space realism wave — report §9 R9): a second, low-albedo DUST field of ridged filaments
   * that ABSORBS (its density dims whatever is behind the nebula — the material switches to premultiplied normal
   * blending, alpha = 1 − transmittance) and scatters the light of an EMBEDDED STAR at the centre through a
   * 2-step light march. 0 (default) keeps the v1 additive emission-only volume bit for bit.
   */
  dust?: number;
  /** v2: the embedded star's colour × intensity (default warm white 2.4). */
  starColor?: [number, number, number];
  /** v2: the dust's albedo (default 0.18 — dark, sooty lanes). */
  dustAlbedo?: number;
}

export interface NebulaUniforms {
  density: { value: number };
  intensity: { value: number };
}

/** Density 0..1 at a unit-sphere local point: fBm falling off to the rim. */
export function nebulaDensityCpu(x: number, y: number, z: number, noise: number): number {
  const r = Math.hypot(x, y, z);
  const falloff = 1 - r * r;
  const f = falloff < 0 ? 0 : falloff;
  const d = (noise * 0.5 + 0.5 - 0.35) * 2.2 * f;
  return d < 0 ? 0 : d > 1 ? 1 : d;
}

/** Beer–Lambert transmittance after a step of density d, length l, absorption k. */
export function beerLambertCpu(d: number, l: number, k: number): number {
  return Math.exp(-d * l * k);
}

/**
 * v2 dust density at a unit-sphere point from a noise sample in [-1, 1]: RIDGED (1 − |n|)^4 — thin filaments where
 * the noise crosses zero — falling off to the rim like the emission, scaled by `dust`.
 */
export function nebulaDustCpu(x: number, y: number, z: number, noise: number, dust: number): number {
  const r2 = x * x + y * y + z * z;
  const falloff = Math.max(0, 1 - r2);
  const ridge = Math.pow(Math.max(0, 1 - Math.abs(noise)), 4);
  return Math.min(1, Math.max(0, ridge * falloff * dust * 1.6));
}

/** v2 in-scatter of the embedded star at distance r (sphere radii) through transmittance tL: albedo · star · tL / (1 + 8r²). */
export function nebulaStarLightCpu(r: number, tL: number, albedo: number, star: number): number {
  return (albedo * star * tL) / (1 + 8 * r * r);
}

export function createNebulaMaterial(config?: NebulaConfig): { material: THREE.NodeMaterial; uniforms: NebulaUniforms } {
  const steps = Math.max(6, Math.min(48, config?.steps ?? 20));
  const cc = config?.coreColor ?? [1.0, 0.55, 0.35];
  const ec = config?.envelopeColor ?? [0.3, 0.45, 1.0];
  const uDensity = uniform(config?.density ?? 1);
  const uIntensity = uniform(config?.intensity ?? 1);
  const absorption = config?.absorption ?? 1.6;
  const scale = config?.scale ?? 2.2;

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  // BACK faces: from outside the far wall is visible and the ray still enters at the near
  // intersection (t0); from INSIDE the camera keeps a wall to draw on (front faces would
  // vanish); double-sided would integrate the segment twice. 
  material.side = THREE.BackSide;
  material.blending = THREE.AdditiveBlending;
  material.fog = false;
  // WHY (shader sweep 2026-10-06, THE OVERLAY LAW): a transparent depthWrite:false layer drawn over far geometry
  // replaced that geometry's normal / velocity / material targets (NoBlending by default) — AO, SSR and the temporal
  // resolve then read this layer's values at the mountain's depth. Marked, it writes alpha-0 zeros to them.
  markMrtOverlay(material);

  const dustK = Math.max(0, config?.dust ?? 0);
  if (dustK > 0) {
    // ── v2 (report §9 R9): emission + an absorbing, star-lit DUST field; premultiplied "over" blending so the dust
    // dims the stars behind it (additive light can never darken anything).
    const sc = config?.starColor ?? [2.4, 2.1, 1.7];
    const albedo = config?.dustAlbedo ?? 0.18;
    const kD = 3.2;
    material.blending = THREE.NormalBlending;
    material.premultipliedAlpha = true;
    const dustAt = (p: any): any => {
      const nd = mx_fractal_noise_float(p.mul(scale * 1.7).add(11.3), 3, 2.0, 0.5, 1.0);
      const ridge = pow(saturate(float(1).sub(abs(nd))), float(4));
      return saturate(ridge.mul(saturate(float(1).sub(dot(p, p)))).mul(dustK * 1.6));
    };
    material.colorNode = Fn(() => {
      const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
      const dir = normalize(positionLocal.sub(camL));
      const b = dot(camL, dir);
      const c = dot(camL, camL).sub(1);
      const sq = sqrt(max(b.mul(b).sub(c), float(0)));
      const t0 = max(b.negate().sub(sq), float(0));
      const t1 = b.negate().add(sq);
      const len = max(t1.sub(t0), float(0));
      const dt = len.div(steps);
      const acc = vec3(0, 0, 0).toVar();
      const trans = float(1).toVar();
      const star = vec3(sc[0], sc[1], sc[2]);
      Loop(steps, ({ i }) => {
        const t = t0.add(dt.mul(float(i as never).add(0.5)));
        const p = camL.add(dir.mul(t));
        const n = mx_fractal_noise_float(p.mul(scale), 4, 2.0, 0.5, 1.0);
        const falloff = saturate(float(1).sub(dot(p, p)));
        const dens = saturate(n.mul(0.5).add(0.5).sub(0.35).mul(2.2).mul(falloff)).mul(uDensity);
        const dust = dustAt(p);
        // The embedded star's light at p: a 2-step march back toward the centre through the dust.
        const r = length(p);
        const tl = exp(dustAt(p.mul(0.66)).add(dustAt(p.mul(0.33))).mul(r).mul(-0.5 * kD));
        const starLit = star.mul(tl).mul(albedo).div(r.mul(r).mul(8).add(1));
        const emit = mix(vec3(ec[0], ec[1], ec[2]), vec3(cc[0], cc[1], cc[2]), saturate(dens.mul(1.6))).mul(dens);
        const core = star.mul(exp(r.mul(r).mul(-60)).mul(6));   // the star itself, shrouded by whatever dust sits in front
        acc.addAssign(emit.add(starLit.mul(dust)).add(core).mul(dt).mul(trans));
        trans.mulAssign(exp(dens.mul(absorption).add(dust.mul(kD)).mul(dt).negate()));
      });
      return vec4(acc.mul(uIntensity).mul(1.8), float(1).sub(trans));
    })();
    return { material: material as THREE.NodeMaterial, uniforms: { density: uDensity as unknown as { value: number }, intensity: uIntensity as unknown as { value: number } } };
  }

  material.colorNode = Fn(() => {
    // Local-space ray from the camera through this fragment (unit sphere).
    const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz;
    const dir = normalize(positionLocal.sub(camL));
    // Sphere entry/exit (|p|=1): solve |camL + t·dir|² = 1.
    const b = dot(camL, dir);
    const c = dot(camL, camL).sub(1);
    const disc = max(b.mul(b).sub(c), float(0));
    const sq = sqrt(disc);
    const t0 = max(b.negate().sub(sq), float(0));
    const t1 = b.negate().add(sq);
    const len = max(t1.sub(t0), float(0));
    const dt = len.div(steps);
    const acc = vec3(0, 0, 0).toVar();
    const trans = float(1).toVar();
    Loop(steps, ({ i }) => {
      const t = t0.add(dt.mul(float(i as never).add(0.5)));
      const p = camL.add(dir.mul(t));
      const n = mx_fractal_noise_float(p.mul(scale), 4, 2.0, 0.5, 1.0);
      const falloff = saturate(float(1).sub(dot(p, p)));
      const dens = saturate(n.mul(0.5).add(0.5).sub(0.35).mul(2.2).mul(falloff)).mul(uDensity);
      const col = mix(vec3(ec[0], ec[1], ec[2]), vec3(cc[0], cc[1], cc[2]), saturate(dens.mul(1.6)));
      acc.addAssign(col.mul(dens).mul(dt).mul(trans));
      trans.mulAssign(exp(dens.mul(dt).mul(-absorption)));
    });
    return vec4(acc.mul(uIntensity).mul(1.8), 1);
  })();

  return { material: material as THREE.NodeMaterial, uniforms: { density: uDensity as unknown as { value: number }, intensity: uIntensity as unknown as { value: number } } };
}
